import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { db, getFCMToken, onForegroundMessage } from '../config/firebase';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { logger } from '../utils/logger';
import { COLLECTIONS } from '../config/constants';

// Bildirim tipleri
export type NotificationType = 
  | 'order_created'      // Sipariş oluşturuldu
  | 'order_preparing'    // Sipariş hazırlanıyor
  | 'order_onway'        // Sipariş yolda
  | 'order_delivered'    // Sipariş teslim edildi
  | 'order_cancelled'    // Sipariş iptal edildi
  | 'subscription_reminder' // Abonelik hatırlatması
  | 'promotion'          // Kampanya/promosyon
  | 'system';            // Sistem bildirimi

// Bildirim veri tipi
export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  icon?: string;
  data?: Record<string, any>; // Ek veri (sipariş ID, vb.)
  read: boolean;
  createdAt: string;
}

// Context tipi
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  fcmToken: string | null;
  permissionGranted: boolean;
  
  // İzin ve token
  requestPermission: () => Promise<boolean>;
  
  // Bildirim işlemleri
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  
  // Admin: Bildirim gönderme
  sendNotification: (userId: string, notification: Omit<Notification, 'id' | 'userId' | 'read' | 'createdAt'>) => Promise<boolean>;
  sendOrderStatusNotification: (userId: string, orderId: string, status: string, orderDetails: string) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

// Sipariş durumu bildirim metinleri
const getOrderStatusNotification = (status: string, orderDetails: string): { title: string; message: string; icon: string } => {
  switch (status) {
    case 'preparing':
      return {
        title: '🔄 Siparişiniz Hazırlanıyor',
        message: `${orderDetails} siparişiniz hazırlanmaya başlandı.`,
        icon: '🔄'
      };
    case 'onway':
      return {
        title: '🚚 Siparişiniz Yola Çıktı!',
        message: `${orderDetails} siparişiniz size doğru yola çıktı.`,
        icon: '🚚'
      };
    case 'delivered':
      return {
        title: '✅ Siparişiniz Teslim Edildi',
        message: `${orderDetails} siparişiniz başarıyla teslim edildi. Afiyet olsun!`,
        icon: '✅'
      };
    case 'cancelled':
      return {
        title: '❌ Sipariş İptal Edildi',
        message: `${orderDetails} siparişiniz iptal edildi.`,
        icon: '❌'
      };
    case 'delayed':
      return {
        title: '⏰ Teslimat Gecikecek',
        message: `${orderDetails} siparişinizin teslimatında gecikme yaşanıyor.`,
        icon: '⏰'
      };
    default:
      return {
        title: '📦 Sipariş Güncellendi',
        message: `${orderDetails} siparişinizde güncelleme var.`,
        icon: '📦'
      };
  }
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const { currentUser } = useAuth();

  // Okunmamış bildirim sayısı
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  // İzin durumunu kontrol et
  useEffect(() => {
    if ('Notification' in window) {
      setPermissionGranted(Notification.permission === 'granted');
    }
  }, []);

  // Service Worker'a Firebase config gönder
  useEffect(() => {
    if ('serviceWorker' in navigator && permissionGranted) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          registration.active.postMessage({
            type: 'FIREBASE_CONFIG',
            config: {
              apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
              authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
              projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
              storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
              messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
              appId: process.env.REACT_APP_FIREBASE_APP_ID,
            }
          });
        }
      });
    }
  }, [permissionGranted]);

  // FCM token al ve kaydet
  useEffect(() => {
    const setupFCM = async () => {
      if (!currentUser || !permissionGranted) return;
      
      try {
        const token = await getFCMToken();
        if (!token) {
          logger.warn('FCM token alınamadı (izin verilmemiş veya destek yok)');
          return;
        }
        setFcmToken(token);

        // Token'ı Firestore'a kaydet — önce users doc, başarısızsa fcmTokens fallback
        try {
          await updateDoc(doc(db, COLLECTIONS.USERS, currentUser.uid), {
            fcmToken: token,
            fcmTokenUpdatedAt: new Date().toISOString(),
          });
        } catch (primaryErr) {
          logger.warn('FCM token users/{uid} dökümanına yazılamadı, fallback fcmTokens deneniyor:', primaryErr);
          try {
            await addDoc(collection(db, 'fcmTokens'), {
              userId: currentUser.uid,
              token,
              createdAt: new Date().toISOString(),
            });
          } catch (fallbackErr) {
            logger.error('FCM token fallback yazımı da başarısız:', fallbackErr);
            // Kullanıcıya bildirme — sessiz fail UX'ini bozmaz, ama log'da görünür
          }
        }
      } catch (error) {
        logger.error('FCM setup error:', error);
      }
    };

    setupFCM();
  }, [currentUser, permissionGranted]);

  // Foreground mesajları dinle
  useEffect(() => {
    if (!permissionGranted) return;

    onForegroundMessage((payload) => {
      // Toast veya in-app bildirim göster
      if (payload.notification) {
        const { title, body } = payload.notification;
        
        // Browser notification göster
        if (Notification.permission === 'granted') {
          new Notification(title || 'Asil Su', {
            body: body || '',
            icon: '/images/logo-icon.png',
            badge: '/images/logo-icon.png',
          });
        }
      }
    });
  }, [permissionGranted]);

  // Firestore'dan bildirimleri dinle
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      return;
    }

    const colRef = collection(db, COLLECTIONS.NOTIFICATIONS);
    const uid = currentUser.uid;

    const mapDocs = (snap: { docs: Array<{ id: string; data: () => any }> }): Notification[] =>
      snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.()?.toISOString() || d.data().createdAt,
        } as Notification))
        .sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1));

    let active = true;
    let unsub: (() => void) | null = null;

    // Tercih edilen: orderBy ile en yeni 50 (composite index: firestore.indexes.json).
    // Index henüz deploy edilmemişse failed-precondition gelir → orderBy'sız sorguya
    // düşüp in-memory sort uygularız. Index deploy edilince otomatik verimli yola geçer.
    const subscribeFallback = () => {
      const qFallback = query(colRef, where('userId', '==', uid), limit(50));
      unsub = onSnapshot(
        qFallback,
        (snap) => setNotifications(mapDocs(snap)),
        (error) => {
          logger.error('Notification dinleme hatası (fallback):', error);
          setNotifications([]);
        }
      );
    };

    const qOrdered = query(colRef, where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(50));
    unsub = onSnapshot(
      qOrdered,
      (snap) => setNotifications(mapDocs(snap)),
      (error) => {
        if ((error as { code?: string }).code === 'failed-precondition') {
          logger.warn('notifications composite index yok — orderBy\'sız fallback. firebase deploy --only firestore:indexes ile düzelir.');
          if (active) subscribeFallback();
          return;
        }
        logger.error('Notification dinleme hatası:', error);
        setNotifications([]);
      }
    );

    return () => {
      active = false;
      unsub?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // İzin iste
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      logger.warn('Bu tarayıcı bildirimleri desteklemiyor');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setPermissionGranted(granted);
      
      if (granted) {
        // Token al
        const token = await getFCMToken();
        if (token) {
          setFcmToken(token);
        }
      }
      
      return granted;
    } catch (error) {
      logger.error('Bildirim izni hatası:', error);
      return false;
    }
  }, []);

  // Bildirimi okundu işaretle
  const markAsRead = useCallback(async (notificationId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), {
        read: true
      });
    } catch (error) {
      logger.error('Bildirim okundu işaretleme hatası:', error);
    }
  }, []);

  // Tümünü okundu işaretle
  const markAllAsRead = useCallback(async (): Promise<void> => {
    const unreadNotifications = notifications.filter(n => !n.read);
    
    try {
      await Promise.all(
        unreadNotifications.map(n => 
          updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, n.id), { read: true })
        )
      );
    } catch (error) {
      logger.error('Tümünü okundu işaretleme hatası:', error);
    }
  }, [notifications]);

  // Bildirimi sil
  const deleteNotification = useCallback(async (notificationId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId));
    } catch (error) {
      logger.error('Bildirim silme hatası:', error);
    }
  }, []);

  // Tüm bildirimleri temizle
  const clearAllNotifications = useCallback(async (): Promise<void> => {
    try {
      await Promise.all(
        notifications.map(n => deleteDoc(doc(db, COLLECTIONS.NOTIFICATIONS, n.id)))
      );
    } catch (error) {
      logger.error('Bildirimleri temizleme hatası:', error);
    }
  }, [notifications]);

  // Bildirim gönder (Admin)
  const sendNotification = useCallback(async (
    userId: string,
    notification: Omit<Notification, 'id' | 'userId' | 'read' | 'createdAt'>
  ): Promise<boolean> => {
    try {
      await addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), {
        userId,
        ...notification,
        read: false,
        createdAt: Timestamp.now()
      });
      return true;
    } catch (error) {
      logger.error('Bildirim gönderme hatası:', error);
      return false;
    }
  }, []);

  // Sipariş durumu bildirimi gönder (Admin)
  const sendOrderStatusNotification = useCallback(async (
    userId: string,
    orderId: string,
    status: string,
    orderDetails: string
  ): Promise<boolean> => {
    const notificationContent = getOrderStatusNotification(status, orderDetails);
    
    const notificationType: NotificationType = 
      status === 'preparing' ? 'order_preparing' :
      status === 'onway' ? 'order_onway' :
      status === 'delivered' ? 'order_delivered' :
      status === 'cancelled' ? 'order_cancelled' : 'order_created';

    return sendNotification(userId, {
      type: notificationType,
      title: notificationContent.title,
      message: notificationContent.message,
      icon: notificationContent.icon,
      data: { orderId, status }
    });
  }, [sendNotification]);

  const contextValue = useMemo<NotificationContextType>(() => ({
    notifications,
    unreadCount,
    fcmToken,
    permissionGranted,
    requestPermission,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    sendNotification,
    sendOrderStatusNotification,
  }), [
    notifications,
    unreadCount,
    fcmToken,
    permissionGranted,
    requestPermission,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    sendNotification,
    sendOrderStatusNotification,
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
